/**
 * Created by @author @ddennis - ddennis.dk aka fantastisk.dk/works aka meresukker.dk on 05/07/2026.
 */
import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react'

import { Alert, Button } from 'antd'
import { BellFilled, CloseOutlined } from '@ant-design/icons'
import { animated, useTransition } from '@react-spring/web'
import { ENDPOINTS } from '../../service/ENDPOINTS'
import { put } from '../../service/API'
import { Link, useLocation, useParams } from 'react-router-dom'
import {
  NOTIFICATION_STATUS,
  NOTIFICATION_STATUS_TYPE,
  notificationToLink,
  NotificationType,
  useNotifications,
} from '../../hooks/useNotifications'

type Props = {
  className?: string
  variant?: 'SUBJECT_VIEW' | null
}

export const NotificationBanner = ({ className, variant }: Props) => {
  const { subjectId } = useParams() as { subjectId: string }

  // remount Content when navigating between subjects so the cached
  // notification and the sent-state ref are reset for the new subject
  return (
    <Suspense>
      <Content key={subjectId} className={className} variant={variant}></Content>
    </Suspense>
  )
}

const Content = ({ className, variant }: Props) => {
  const { subjectId, projectId } = useParams() as { subjectId: string; projectId: string }
  const { latestNotification, activeNotifications, mutate } = useNotifications(projectId, false, subjectId)
  const sentState = useRef<NOTIFICATION_STATUS_TYPE | null>(null)
  const loc = useLocation()

  // a litte trick to keep the alert showing even after we have updated it
  const [cache] = useState(latestNotification)
  const notification = variant === 'SUBJECT_VIEW' ? cache : latestNotification

  const updateNotification = useCallback(
    (state: NOTIFICATION_STATUS_TYPE) => {
      if (sentState.current === NOTIFICATION_STATUS.SEEN) {
        return
      }
      sentState.current = state

      const obj = { notificationId: notification._id, state: state }

      const p = ENDPOINTS.PROJECTS.UPDATE_NOTIFICATION(notification._id)
      put(p, obj)
        .then((result) => {
          mutate()
          return result
        })
        .catch(() => {
          alert('Notification was not updated')
        })
    },
    [mutate, notification]
  )

  const isCurrnetSubject = notification ? notification.target.subjectId._id === subjectId : false

  useEffect(() => {
    const viewSubject = loc.pathname.indexOf('/subject/view/' + subjectId) > -1

    if (isCurrnetSubject && viewSubject && notification?._id) {
      updateNotification(NOTIFICATION_STATUS.SEEN)
    }
  }, [notification?._id, loc.pathname, subjectId, updateNotification, isCurrnetSubject])

  // the id we dismissed locally - hides that banner while mutate() brings in the next notification
  const [dismissedId, setDismissedId] = useState<string | null>(null)

  // only show the alert when on the project list or the current project
  const isProjectList = loc.pathname === '/'
  const isCurrentProject = isProjectList ? true : notification?.projectId === projectId

  const visible =
    notification &&
    notification._id !== dismissedId &&
    isCurrentProject &&
    (variant !== 'SUBJECT_VIEW' || isCurrnetSubject)
      ? [notification]
      : []

  // keyed by id: the dismissed banner animates out and the next one animates in
  const transitions = useTransition(visible, {
    keys: (n: NotificationType) => n._id,
    from: { opacity: 0, transform: 'translate3d(0,-5px,0)' },
    enter: { opacity: 1, transform: 'translate3d(0,5px,0)' },
    leave: { opacity: 0, transform: 'translate3d(0,-5px,0)' },
    config: { /*easing: easings.easeOutExpo,*/ friction: 24, tension: 240 },
    exitBeforeEnter: true,
  })

  const onClose = () => {
    if (!notification) {
      return
    }
    setDismissedId(notification._id)
    const newType = variant === 'SUBJECT_VIEW' ? NOTIFICATION_STATUS.SEEN : NOTIFICATION_STATUS.DISMISSED
    updateNotification(newType)
  }

  return transitions((style, n) => {
    const isTargetSubject = n.target.subjectId._id === subjectId

    const title = isTargetSubject ? n.configId.content.target.title : n.configId.content.banner.title
    const body = isTargetSubject ? n.configId.content.target.body : n.configId.content.banner.body
    const buttonTxt = n.configId.content.banner.button
    const link = notificationToLink(n)

    return variant === 'SUBJECT_VIEW' ? (
      <animated.div style={style}>
        <SubjectViewBanner
          className={className}
          title={title}
          body={body}
          isCurrnetSubject={isTargetSubject}
          onClose={onClose}
        />
      </animated.div>
    ) : (
      <animated.div style={style}>
        <div className={className ? className : 'container-fluid px-0 mt-4'}>
          <div className="row" style={{}}>
            <div className="col-12 px-0">
              <Alert
                showIcon
                icon={<BellFilled></BellFilled>}
                style={{ boxShadow: '0 2px 15px -2px rgba(0, 0, 0, 0.15)' }}
                closable={{ onClose: onClose, closeIcon: <CloseOutlined style={{ fontSize: 16 }} /> }}
                title={
                  <div className="">
                    <p className="opacity-75 p-mini">Alert 1/{activeNotifications.length}</p>
                    <h6 className="">{title}</h6>
                  </div>
                }
                description={
                  <div className="">
                    <p className="mb-2 opacity-75">
                      {body} <span className="fw-bold">Participant {n.target.subjectId.displayId}</span>
                    </p>

                    <div className="d-flex pt-1">
                      <Button type="default" onClick={onClose} className="me-2">
                        Dismiss
                      </Button>
                      <Link to={link}>
                        <Button type="primary">{buttonTxt}</Button>
                      </Link>
                    </div>
                  </div>
                }
                type="info"
              />
            </div>
          </div>
        </div>
      </animated.div>
    )
  })
}

type SubjectViewBannerProps = {
  className?: string
  title: string
  body: string
  isCurrnetSubject: boolean

  onClose: () => void
}

const SubjectViewBanner = ({
  className,
  title,
  body,
  isCurrnetSubject,

  onClose,
}: SubjectViewBannerProps) => {
  if (!isCurrnetSubject) {
    return null
  }
  return (
    <div className={className ? className : 'container-fluid px-0 mt-4'}>
      <div className="row" style={{}}>
        <div className="col-12 px-0">
          <Alert
            showIcon
            icon={<BellFilled></BellFilled>}
            style={{ boxShadow: '0 1px 10px -2px rgba(0, 0, 0, 0.2)' }}
            closable={{ onClose: onClose, closeIcon: <CloseOutlined style={{ fontSize: 16 }} /> }}
            title={
              <p className="">
                <span className="fw-bold me-1">{title}</span> {body}
              </p>
            }
            type="warning"
          />
        </div>
      </div>
    </div>
  )
}
